<template>
    <div>
        <label v-if="label" class="vs-select--label">{{ label }}</label>

        <div class="relative flex flex-row gap-2">
            <div class="relative flex-1" v-click-outside="closeDropdown">
                <div
                    @click="toggleDropdown"
                    @keydown.enter.prevent="toggleDropdown"
                    @keydown.space.prevent="toggleDropdown"
                    @keydown.down.prevent="openDropdown"
                    @keydown.esc.prevent="closeDropdown"
                    class="bg-white border rounded-lg p-2 flex items-center justify-between cursor-pointer"
                    :class="{ 'border-danger': error, 'border-grey': !error, 'input-disabled': disabled }"
                    tabindex="0"
                    role="combobox"
                    :aria-expanded="isOpen"
                    aria-owns="dropdown-"
                    :aria-activedescendant="activeDescendant"
                >
                    <div class="flex flex-wrap gap-1 overflow-hidden">
                        <template v-if="multiple">
                            <!-- Sortable Chips -->
                            <draggable
                                v-if="sortable"
                                v-model="selectedOptionsInternal"
                                :options="{ animation: 150, handle: '.chip-handle' }"
                                @end="onSortEnd"
                                class="flex flex-wrap gap-1 overflow-hidden"
                            >
                                <vs-chip
                                    v-for="option in selectedOptionsInternal.slice(0, maxElementsToShow)"
                                    :key="option.key"
                                    color="primary"
                                    class="mb-0 rounded-md chip flex items-center"
                                    closable
                                    @click="removeOption(option)"
                                >
                                    <span class="chip-handle cursor-move mr-1">≡</span>
                                    {{ option.text }}
                                </vs-chip>
                            </draggable>

                            <!-- Regular Chips (when not sortable) -->
                            <template v-else>
                                <vs-chip
                                    v-for="option in selectedOptions.slice(0, maxElementsToShow)"
                                    :key="option.key"
                                    color="primary"
                                    class="mb-0 rounded-md chip"
                                    closable
                                    @click="removeOption(option)"
                                >
                                    {{ option.text }}
                                </vs-chip>
                            </template>

                            <!-- Overflow counter -->
                            <div v-if="selectedOptions.length > maxElementsToShow">
                                {{ selectedOptions.length - maxElementsToShow + ' more' }}
                            </div>

                            <!-- Placeholder when nothing selected -->
                            <span v-if="selectedOptions.length === 0">{{ placeholder }}</span>
                        </template>

                        <template v-else>
                            <span class="text-black">{{ selectedOptions.length ? selectedOptions[0].text : placeholder }}</span>
                        </template>
                    </div>
                    <div class="w-3">
                        <svg class="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M19 9l-7 7-7-7"></path>
                        </svg>
                    </div>

                </div>
                <div
                    v-if="isOpen"
                    class="absolute w-full mt-1 bg-white border border-grey rounded-lg overflow-hidden"
                    style="z-index: 10000"
                >
                    <input
                        ref="searchInput"
                        v-model="search"
                        @input="filterOptions"
                        @keydown.down.prevent="navigateOptions('down')"
                        @keydown.up.prevent="navigateOptions('up')"
                        @keydown.enter.prevent="selectFocusedOption"
                        @keydown.esc.prevent="closeDropdown"
                        class="w-full p-2 border-t-0 border-l-0 border-r-0 border-b-1 border-grey rounded-lg focus:outline-none"
                        placeholder="Search options..."
                    />
                    <div>
                        <ul
                            id="dropdown"
                            role="listbox"
                            :aria-multiselectable="multiple"
                            :style="{maxHeight: condensedOptions ? '120px' : '300px'}"
                            class="overflow-y-scroll listbox"
                            @scroll="handleScroll"
                        >
                            <li
                                v-for="(option, index) in filteredOptions"
                                :key="option.key"
                                :id="'option-' + option.key"
                                @click="toggleOption(option)"
                                @keydown.enter.prevent="toggleOption(option)"
                                @keydown.space.prevent="toggleOption(option)"
                                :ref="'option-' + index"
                                :class="{
                                'p-2 cursor-pointer hover:bg-primary hover:text-white': true,
                                'bg-primary text-white': index === focusedOptionIndex || isSelected(option),
                                'opacity-50 cursor-not-allowed': isDisabled(option),
                            }"
                                :tabindex="isDisabled(option) ? -1 : 0"
                                role="option"
                                :aria-selected="isSelected(option)"
                                :aria-disabled="isDisabled(option)"
                            >
                                <template v-if="multiple">
                                    <input
                                        type="checkbox"
                                        :checked="isSelected(option)"
                                        :disabled="isDisabled(option)"
                                        class="mr-2 hidden"
                                    />
                                </template>
                                {{ option.text }}
                            </li>
                            <li
                                v-if="loading && filteredOptions.length === 0"
                                class="p-2 cursor-pointer hover:bg-primary hover:text-white"
                            >
                                Loading results...
                            </li>
                            <li
                                v-if="loading && filteredOptions.length > 1"
                                class="p-2 cursor-pointer hover:bg-primary hover:text-white"
                            >
                                Loading more results...
                            </li>
                            <li
                                v-if="!loading && filteredOptions.length === 0"
                                class="p-2 cursor-pointer hover:bg-primary hover:text-white"
                            >
                                No results
                            </li>
                        </ul>
                    </div>
                </div>
                <p v-if="error" class="text-xs text-red mt-1 pl-1 top-0 text-danger">{{ this.$t(errorMessage) }}</p>
            </div>
            <feather-icon v-if="cleareable" icon="XIcon" svgClasses="text-danger align-middle w-6" class="align-middle cursor-pointer" @click="clear"/>
        </div>

    </div>
</template>

<script>

import draggable from 'vuedraggable'

export default {
    components: { draggable },
    props: {
        options: {
            type: Array,
            required: true,
            validator: (value) => value.every(option => 'key' in option && 'text' in option)
        },
        disabledOptionIds: {
            type: Array,
            default: () => []
        },
        condensedOptions: {
            type: Boolean,
            default: false
        },
        cleareable: {
            type: Boolean,
            default: false
        },
        value: {
            type: [Array, String, Number],
            default: () => []
        },
        multiple: {
            type: Boolean,
            default: false
        },
        sortable: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        error: {
            type: Boolean,
            default: false
        },
        errorMessage: {
            type: String,
            default: "Select an option"
        },
        placeholder: {
            type: String,
            default: 'Select option'
        },
        label: {
            default: null
        },
        asyncFirstFetchCallback: {
            type: Function,
            default: null,
            required: false,
        },
        asyncNextFetchCallback: {
            type: Function,
            default: null,
            required: false,
        }
    },
    data() {
        return {
            isOpen: false,
            search: '',
            filteredOptions: [],
            focusedOptionIndex: -1,
            activeDescendant: null,
            loading: false,
            firstOpen: true,
            internalOptions: [],
            maxElementsToShow: 10,
            selectedOptionsInternal: []
        }
    },
    computed: {
        selectedOptions() {
            if (this.multiple) {
                const valueArray = Array.isArray(this.value) ? this.value : []

                return valueArray
                    .map(key => this.internalOptions.find(option => option.key === key))
                    .filter(Boolean)
            } else {
                return this.internalOptions.filter(option => option.key === this.value)
            }
        }
    },
    watch: {
        options: {
            immediate: true,
            handler(newOptions) {
                const newOptionsCopy = JSON.parse(JSON.stringify(newOptions))

                if (this.multiple) {
                    const valueArray = Array.isArray(this.value) ? this.value : []

                    const selected = valueArray
                        .map(key => newOptionsCopy.find(o => o.key === key))
                        .filter(Boolean)

                    const unselected = newOptionsCopy.filter(
                        option => !valueArray.includes(option.key)
                    )

                    this.internalOptions = [...selected, ...unselected]
                } else {
                    const selected = newOptionsCopy.find(o => o.key === this.value)
                    this.internalOptions = selected
                        ? [selected, ...newOptionsCopy.filter(o => o.key !== this.value)]
                        : newOptionsCopy
                }

                this.filteredOptions = this.internalOptions
            }
        },
        selectedOptions: {
            immediate: true,
            handler(newVal) {
                this.selectedOptionsInternal = [...newVal]
            }
        }
    },
    directives: {
        clickOutside: {
            bind(el, binding, vnode) {
                el.clickOutsideEvent = function(event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        vnode.context[binding.expression](event)
                    }
                }
                document.body.addEventListener('click', el.clickOutsideEvent)
            },
            unbind(el) {
                document.body.removeEventListener('click', el.clickOutsideEvent)
            }
        }
    },
    methods: {
        clear() {
            this.value = this.multiple ? [] : null
            this.$emit('input', this.value)
        },
        toggleDropdown() {
            if (this.disabled) return
            this.isOpen = !this.isOpen
            if (this.isOpen) {
                this.$nextTick(() => {
                    this.$refs.searchInput.focus()
                })
                if (this.firstOpen) {
                    this.firstOpen = false
                    this.callFirstAsyncCallback()
                }
            }
        },
        openDropdown() {
            if (!this.isOpen) {
                this.isOpen = true
                this.$nextTick(() => {
                    this.$refs.searchInput.focus()
                })
            }
        },
        closeDropdown() {
            this.isOpen = false
            this.focusedOptionIndex = -1
        },
        toggleOption(option) {
            if (this.isDisabled(option)) return

            if (this.multiple) {
                const newValue = this.isSelected(option)
                    ? this.value.filter(key => key !== option.key)
                    : [...this.value, option.key]
                this.$emit('input', newValue)
            } else {
                this.$emit('input', option.key)
                this.closeDropdown()
            }
        },
        removeOption(option) {
            if (this.multiple) {
                this.$emit('input', this.value.filter(key => key !== option.key))
            } else {
                this.$emit('input', null)
            }
        },
        filterOptions() {
            if (this.asyncFirstFetchCallback) {
                this.callFirstAsyncCallback(this.search.toLowerCase())
                return
            }

            this.filteredOptions = this.internalOptions.filter(option =>
                option.text.toLowerCase().includes(this.search.toLowerCase())
            )
            this.focusedOptionIndex = -1
        },
        isSelected(option) {
            return this.multiple
                ? this.value.includes(option.key)
                : this.value === option.key
        },
        isDisabled(option) {
            return this.disabledOptionIds.includes(option.key)
        },
        navigateOptions(direction) {
            if (direction === 'down') {
                this.focusedOptionIndex = (this.focusedOptionIndex + 1) % this.filteredOptions.length
            } else if (direction === 'up') {
                this.focusedOptionIndex = (this.focusedOptionIndex - 1 + this.filteredOptions.length) % this.filteredOptions.length
            }
            this.scrollToFocusedOption()
            this.updateActiveDescendant()
        },
        scrollToFocusedOption() {
            this.$nextTick(() => {
                const focusedOption = this.$refs[`option-${this.focusedOptionIndex}`]
                if (focusedOption && focusedOption[0]) {
                    focusedOption[0].scrollIntoView({ block: 'nearest' })
                }
            })
        },
        updateActiveDescendant() {
            const focusedOption = this.filteredOptions[this.focusedOptionIndex]
            this.activeDescendant = focusedOption ? `option-${focusedOption.key}` : null
        },
        selectFocusedOption() {
            if (this.focusedOptionIndex >= 0 && this.focusedOptionIndex < this.filteredOptions.length) {
                this.toggleOption(this.filteredOptions[this.focusedOptionIndex])
            }
        },
        handleScroll(ev) {
            const container = ev.target;
            const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight;

            if (atBottom) {
                this.callNextAsyncCallback()
            }
        },
        async callFirstAsyncCallback() {
            if (!this.asyncFirstFetchCallback || this.loading) {
                return
            }

            this.loading = true
            this.asyncFirstFetchCallback(this.search)
                .then(() => this.loading = false)
                .catch(() => this.loading = false)
        },

        async callNextAsyncCallback() {
            if (!this.asyncNextFetchCallback || this.loading) {
                return
            }

            this.loading = true
            this.asyncNextFetchCallback(this.search)
                .then(() => this.loading = false)
                .catch(() => this.loading = false)
        },
        onSortEnd() {
            const newValue = this.selectedOptionsInternal.map(opt => opt.key)
            this.$emit('input', newValue)
        }
    }
}
</script>

<style scoped>
    .listbox {
        scrollbar-color: #3560DA white !important;
        scrollbar-width: thin;
    }

    .input-disabled {
        cursor: not-allowed !important;
        border: 1px solid rgba(0, 0, 0, 0.2) !important;
        background-color: rgba(239, 239, 239, 0.3) !important;
        color: rgb(170, 170, 170) !important;
    }
</style>
